/**
 * Business Office write-back confirmation modal with optional linked sibling
 * orders table when filtered siblings already exist in context.
 * @module triggerRpaNewModal
 */
import { Modal } from "@risalabs_frontend_org/oasis-ui-kit";
import { useState } from "react";
import { ModalId } from "../../../enums/modalId";
import WarningIcon from "../../../svg/warningIcon";
import CustomCheckBox from "../../customCheckBox/customCheckBox";
import { useLinkedOrdersModalView } from "../../expandableTableForOrderAuth/hooks/useLinkedOrdersModalView";
import RenderSiblingOrderContentBody from "../updateCigNoteConfirmationDialog/renderSiblingOrderContentBody";

/** Props for {@link TriggerRpaNewModal}. */
interface TriggerRpaNewModalProps {
  /** Fires when the user confirms write-back / trigger RPA. */
  onTriggerRpa: (selectedOrderIds?: string[]) => void;
  /** Closes the modal without saving. */
  onClose: () => void;
  /** When true, disables save and shows a loading label on the primary button. */
  loading?: boolean;
}

const DEFAULT_TITLE = "Trigger RPA";

const actionables = [
  {
    label: "Individual drug's auth status is up to date & correct.",
    value: "auth_status_up_to_date",
  },
  {
    label: "Notes are up to date with correct Dx Code, Auth ID.",
    value: "notes_up_to_date",
  },
  {
    label: "Radio button(s) selected is/are correct.",
    value: "radio_buttons_selected",
  },
];

/**
 * Confirms Business Office write-back. Shows linked sibling orders when
 * `filteredSiblingOrders` is already populated in context; otherwise shows
 * the legacy checkbox checklist. Sibling order checkboxes stay interactive.
 *
 * @param props - Trigger handler, close handler, and optional loading flag
 * @returns Trigger RPA confirmation modal
 */
const TriggerRpaNewModal = ({
  onTriggerRpa,
  onClose,
  loading = false,
}: TriggerRpaNewModalProps) => {
  const [selectedActionables, setSelectedActionables] = useState<string[]>([]);
  const {
    expandableTableData,
    siblingOrdersError,
    hasLinkedOrders,
    selectedSiblingOrderIds,
  } = useLinkedOrdersModalView();

  const handleModalSave = () => {
    onTriggerRpa(
      expandableTableData.length > 0 && selectedSiblingOrderIds.length > 0
        ? selectedSiblingOrderIds
        : undefined,
    );
  };

  return (
    <Modal
      dialogId={ModalId.TRIGGER_RPA_MODAL_NEW}
      onSave={handleModalSave}
      title={DEFAULT_TITLE}
      saveButtonText={loading ? "Triggering..." : "Trigger RPA"}
      cancelText="Cancel"
      onClose={onClose}
      onCancel={onClose}
      disableSave={
        loading ||
        (!hasLinkedOrders && selectedActionables.length < actionables.length)
      }
      maxHeightPercentage={hasLinkedOrders ? 80 : 50}
    >
      <div className="trigger-rpa-new-model--container flex flex-col gap-4">
        {hasLinkedOrders ? (
          <RenderSiblingOrderContentBody
            expandedContentType="notes"
            expandableTableData={expandableTableData}
            siblingOrdersLoading={false}
            siblingOrdersError={siblingOrdersError}
            descriptionText="Trigger for following orders as well?"
          />
        ) : (
          <>
            <div className="trigger-rpa-mew-model--information flex items-center gap-2 bg-[#FAE6E6] p-2 text-small">
              <WarningIcon outerFillColor="black" height="16" width="16" />
              <div>
                Make sure you have completed the following, this step can't be{" "}
                <span className="font-bold">reversed</span>
              </div>
            </div>
            <div className="trigger-rpa-new-model--actionables flex flex-col gap-3">
              <div className="header text-small">
                Select the actions that have been taken
              </div>
              <div className="actionables mb-4 flex flex-col gap-2">
                {actionables.map((actionable) => (
                  <CustomCheckBox
                    label={actionable.label}
                    isChecked={selectedActionables.includes(actionable.value)}
                    onCheckBoxValueChange={(data) => {
                      setSelectedActionables(
                        data.value
                          ? [...selectedActionables, data.name]
                          : selectedActionables.filter(
                              (item) => item !== data.name,
                            ),
                      );
                    }}
                    id={actionable.value}
                    key={actionable.value}
                    className="!mt-[3px] text-small"
                  />
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default TriggerRpaNewModal;
